<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <!-- ── CHAT HEADER ── -->
    <div class="chat-header">
      <button class="back-btn" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="15 18 9 12 15 6"/></svg>
      </button>

      <div class="header-avatar" @click="goToProfile">
        <img v-if="friend.image" :src="friend.image" :alt="friend.fullName" />
        <span v-else>{{ (friend.fullName || '?').charAt(0) }}</span>
      </div>

      <div class="header-meta" @click="goToProfile">
        <p class="header-name">{{ friend.fullName }}</p>
        <p class="header-role">{{ friend.title || friend.role || 'Member' }}</p>
      </div>
    </div>

    <!-- ── MESSAGES AREA ── -->
    <div class="messages-area" ref="messagesContainer" @click="openMenuMessageId = null">

      <div v-if="filteredMessages.length === 0" class="messages-empty">
        <div class="empty-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="empty-sub">Begin the correspondence</p>
      </div>

      <div
        v-for="message in filteredMessages"
        :key="message.id"
        class="msg-row"
        :class="{ mine: message.senderId === currentUser.uid, theirs: message.senderId !== currentUser.uid }"
      >
        <div
          v-if="message.senderId !== currentUser.uid"
          class="msg-avatar"
        >
          <img v-if="friend.image" :src="friend.image" :alt="friend.fullName" />
          <span v-else>{{ (friend.fullName || '?').charAt(0) }}</span>
        </div>

        <div class="bubble" :class="{ mine: message.senderId === currentUser.uid, theirs: message.senderId !== currentUser.uid }">
          <!-- Shared recommendation card -->
          <div v-if="message.sharedPost" class="shared-post" @click="viewSharedPost(message.sharedPost.id)">
            <div class="shared-post-tags">
              <span class="shared-post-tag">{{ message.sharedPost.city }}</span>
              <span class="shared-post-tag">{{ message.sharedPost.category }}</span>
            </div>
            <p class="shared-post-name">{{ message.sharedPost.businessName }}</p>
            <p class="shared-post-cta">View Recommendation →</p>
          </div>
          <p v-if="message.text" class="bubble-text">
            {{ message.text }}
            <span v-if="message.isEdited" class="bubble-edited">(edited)</span>
          </p>
          <div class="bubble-footer">
            <span class="bubble-time">{{ formatTimestamp(message.timestamp) }}</span>
            <button
              v-if="message.senderId === currentUser.uid"
              class="bubble-menu-btn"
              @click.stop="toggleMessageMenu(message)"
            >⋯</button>
          </div>

          <div v-if="openMenuMessageId === message.id" class="bubble-menu">
            <button
              v-if="!message.sharedPost && message.text"
              class="bubble-menu-item"
              @click.stop="editMessage(message)"
            >Edit</button>
            <button class="bubble-menu-item destructive" @click.stop="deleteMessage(message)">Delete</button>
          </div>
        </div>
      </div>

    </div>

    <!-- ── EDIT MESSAGE ── -->
    <div v-if="editingMessage" class="overlay" @click.self="cancelEdit">
      <div class="edit-panel">
        <div class="edit-panel-hd">Edit Message</div>
        <textarea v-model="editText" class="edit-textarea" rows="3" @keyup.enter.exact="saveEdit"></textarea>
        <div class="edit-actions">
          <button class="edit-cancel" @click="cancelEdit">Cancel</button>
          <button class="edit-save" @click="saveEdit">Save</button>
        </div>
      </div>
    </div>

    <!-- ── SHARE RECOMMENDATION PICKER ── -->
    <div v-if="showSharePicker" class="overlay" @click.self="showSharePicker = false">
      <div class="share-panel">
        <div class="share-panel-hd">
          <span>Share a Recommendation</span>
          <button class="share-close" @click="showSharePicker = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="share-search-wrap">
          <input
            v-model="shareSearchText"
            class="share-search-input"
            type="text"
            placeholder="Search by place, city, or category"
          />
        </div>
        <div v-if="isLoadingRecommendations" class="share-loading">Loading recommendations…</div>
        <div v-else-if="availableRecommendations.length === 0" class="share-empty">
          <p>No public recommendations yet — be the first to share one.</p>
          <button class="share-new-btn" @click="openCreateRecommendation">+ New Recommendation</button>
        </div>
        <div v-else-if="filteredRecommendations.length === 0" class="share-empty">
          No recommendations match "{{ shareSearchText }}".
        </div>
        <div v-else class="share-list">
          <div
            v-for="rec in filteredRecommendations"
            :key="rec.id"
            class="share-item"
            @click="shareRecommendation(rec)"
          >
            <p class="share-item-tags">{{ rec.city }} · {{ rec.category }}</p>
            <p class="share-item-name">{{ rec.businessName }}</p>
            <p v-if="rec.fullName" class="share-item-by">{{ rec.fullName }}</p>
          </div>
        </div>
        <button v-if="availableRecommendations.length > 0" class="share-new-btn share-new-btn-footer" @click="openCreateRecommendation">
          + New Recommendation
        </button>
      </div>
    </div>

    <!-- ── INPUT BAR ── -->
    <div class="input-bar">
      <button class="share-btn" title="Share a recommendation" @click="openSharePicker">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
      </button>
      <input
        v-model="newMessage"
        class="msg-input"
        type="text"
        placeholder="Write a message…"
        @keyup.enter="sendMessage"
      />
      <div class="input-line"></div>
      <button
        class="send-btn"
        :disabled="!newMessage.trim()"
        @click="sendMessage"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
      </button>
    </div>
  </div>
</template>

<script>
import {
  collection, addDoc, query, where,
  onSnapshot, getDocs, updateDoc, doc, getDoc, deleteDoc, Timestamp,
} from "firebase/firestore";
import { db, auth } from "@/firebase";
import { format } from "date-fns";

export default {
  name: "ChatView",

  data() {
    return {
      friend: {},
      messages: [],
      newMessage: "",
      currentUser: {},
      showSharePicker: false,
      availableRecommendations: [],
      isLoadingRecommendations: false,
      shareSearchText: "",
      editingMessage: null,
      editText: "",
      openMenuMessageId: null,
      sentMessages: [],
      receivedMessages: [],
      sentListenerUnsub: null,
      receivedListenerUnsub: null,
    };
  },

  computed: {
    filteredMessages() {
      return this.messages
        .filter(
          (msg) =>
            (msg.senderId === this.currentUser.uid && msg.receiverUid === this.friend.id) ||
            (msg.senderId === this.friend.id && msg.receiverUid === this.currentUser.uid)
        )
        .sort((a, b) => (a.timestamp?.toMillis?.() || 0) - (b.timestamp?.toMillis?.() || 0));
    },

    filteredRecommendations() {
      const q = this.shareSearchText.trim().toLowerCase();
      if (!q) return this.availableRecommendations;
      return this.availableRecommendations.filter((rec) => {
        const haystack = `${rec.businessName || ""} ${rec.city || ""} ${rec.category || ""}`.toLowerCase();
        return haystack.includes(q);
      });
    },
  },

  async mounted() {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    this.currentUser = {
      uid: currentUser.uid,
      displayName: currentUser.displayName,
      photoURL: currentUser.photoURL,
    };

    await this.fetchFriend();
    if (this.friend.id) {
      this.listenForMessages();
      await this.markMessagesAsRead();
    }
  },

  beforeUnmount() {
    if (this.sentListenerUnsub) this.sentListenerUnsub();
    if (this.receivedListenerUnsub) this.receivedListenerUnsub();
  },

  methods: {
    async fetchFriend() {
      const friendId = this.$route.params.friendId;
      if (!friendId) return;
      const friendSnap = await getDoc(doc(db, "user", friendId));
      if (friendSnap.exists()) {
        this.friend = { id: friendSnap.id, ...friendSnap.data() };
      }
    },

    listenForMessages() {
      if (this.sentListenerUnsub) this.sentListenerUnsub();
      if (this.receivedListenerUnsub) this.receivedListenerUnsub();
      this.sentMessages = [];
      this.receivedMessages = [];

      const sentQ = query(
        collection(db, "messages"),
        where("senderId", "==", this.currentUser.uid),
        where("receiverUid", "==", this.friend.id)
      );
      this.sentListenerUnsub = onSnapshot(sentQ, (snapshot) => {
        this.sentMessages = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        this.mergeAndScroll();
      });

      const receivedQ = query(
        collection(db, "messages"),
        where("senderId", "==", this.friend.id),
        where("receiverUid", "==", this.currentUser.uid)
      );
      this.receivedListenerUnsub = onSnapshot(receivedQ, (snapshot) => {
        this.receivedMessages = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        this.mergeAndScroll();
      });
    },

    mergeAndScroll() {
      this.messages = [...this.sentMessages, ...this.receivedMessages].sort(
        (a, b) => (a.timestamp?.toMillis?.() || 0) - (b.timestamp?.toMillis?.() || 0)
      );
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) container.scrollTop = container.scrollHeight;
      });
    },

    async sendMessage() {
      if (!this.newMessage.trim()) return;
      const message = {
        text: this.newMessage.trim(),
        senderId: this.currentUser.uid,
        senderName: this.currentUser.displayName || "",
        senderImage: this.currentUser.photoURL || "",
        receiverUid: this.friend.id,
        timestamp: Timestamp.now(),
        received: false,
        read: false,
        kind: "text",
      };
      await addDoc(collection(db, "messages"), message);
      this.newMessage = "";
    },

    formatTimestamp(timestamp) {
      try {
        return format(timestamp.toDate(), "p, MMM d");
      } catch {
        return "";
      }
    },

    async markMessagesAsRead() {
      const q = query(
        collection(db, "messages"),
        where("senderId", "==", this.friend.id),
        where("receiverUid", "==", this.currentUser.uid),
        where("received", "==", false)
      );
      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        await updateDoc(doc(db, "messages", docSnap.id), { received: true, read: true });
      }
    },

    openCreateRecommendation() {
      // TODO: wire this to PostCreateDialog.vue in chat-only/private mode,
      // matching iOS's NewPostView onChatOnlySave / Android's
      // NewPostActivity chatOnlyMode. Needs PostCreateDialog.vue's current
      // state to integrate correctly rather than guessing at its props.
      console.warn("openCreateRecommendation: not yet wired up — needs PostCreateDialog.vue");
    },

    async editMessage(message) {
      this.editingMessage = message;
      this.editText = message.text;
      this.openMenuMessageId = null;
    },

    async saveEdit() {
      const trimmed = this.editText.trim();
      if (!trimmed || !this.editingMessage) {
        this.editingMessage = null;
        return;
      }
      await updateDoc(doc(db, "messages", this.editingMessage.id), {
        text: trimmed,
        isEdited: true,
      });
      this.editingMessage = null;
    },

    cancelEdit() {
      this.editingMessage = null;
    },

    async deleteMessage(message) {
      this.openMenuMessageId = null;
      await deleteDoc(doc(db, "messages", message.id));
    },

    toggleMessageMenu(message) {
      if (message.senderId !== this.currentUser.uid) return;
      this.openMenuMessageId = this.openMenuMessageId === message.id ? null : message.id;
    },

    goToProfile() {
      if (this.friend.id) {
        this.$router.push({ name: "MemberDetail", params: { id: this.friend.id } });
      }
    },

    async openSharePicker() {
      this.showSharePicker = true;
      if (this.availableRecommendations.length) return;
      this.isLoadingRecommendations = true;
      try {
        const q = query(collection(db, "explore"), where("isPublic", "==", true));
        const snap = await getDocs(q);
        this.availableRecommendations = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((post) => post.postType !== "travel_plan");
      } catch (err) {
        console.error("Failed to load recommendations:", err);
      } finally {
        this.isLoadingRecommendations = false;
      }
    },

    async shareRecommendation(rec) {
      const message = {
        text: "",
        senderId: this.currentUser.uid,
        senderName: this.currentUser.displayName || "",
        senderImage: this.currentUser.photoURL || "",
        receiverUid: this.friend.id,
        timestamp: Timestamp.now(),
        received: false,
        read: false,
        kind: "shared_post",
        sharedPost: {
          id: rec.id,
          city: rec.city || "",
          category: rec.category || "",
          businessName: rec.businessName || "",
        },
      };
      await addDoc(collection(db, "messages"), message);
      this.showSharePicker = false;
    },

    viewSharedPost(postId) {
      this.$router.push({ name: "PostDetail", params: { postId } });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; display: flex; flex-direction: column; overflow: hidden; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 800px; height: 600px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.07) 0%, transparent 65%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.4; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

/* ── HEADER ── */
.chat-header { position: relative; z-index: 10; display: flex; align-items: center; gap: 14px; padding: 20px 32px; border-bottom: 1px solid rgba(210,175,100,0.1); background: rgba(7,7,8,0.95); flex-shrink: 0; }
.back-btn { background: none; border: none; color: rgba(210,175,100,0.4); cursor: pointer; display: flex; padding: 4px; transition: color 0.2s; flex-shrink: 0; }
.back-btn:hover { color: rgba(210,175,100,0.8); }
.back-btn svg { width: 18px; height: 18px; }
.header-avatar { width: 44px; height: 44px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.25); overflow: hidden; background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 18px; font-weight: 300; color: rgba(210,175,100,0.5); flex-shrink: 0; cursor: pointer; transition: border-color 0.2s; }
.header-avatar:hover { border-color: rgba(210,175,100,0.5); }
.header-avatar img { width: 100%; height: 100%; object-fit: cover; }
.header-meta { cursor: pointer; }
.header-name { font-family: 'Cormorant', serif; font-size: 22px; font-weight: 400; color: #edeae2; line-height: 1.1; margin-bottom: 2px; transition: color 0.2s; }
.header-meta:hover .header-name { color: rgba(210,175,100,0.8); }
.header-role { font-size: 9px; font-weight: 500; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.35); }

/* ── MESSAGES AREA ── */
.messages-area { position: relative; z-index: 5; flex: 1; overflow-y: auto; padding: 28px 32px; display: flex; flex-direction: column; gap: 16px; scrollbar-width: thin; scrollbar-color: rgba(210,175,100,0.15) transparent; }
.messages-area::-webkit-scrollbar { width: 2px; }
.messages-area::-webkit-scrollbar-thumb { background: rgba(210,175,100,0.15); }

.messages-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; gap: 16px; padding: 60px 0; }
.empty-seal { position: relative; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.12); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 10px; height: 10px; border: 1px solid rgba(210,175,100,0.5); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.25); }
.empty-sub { font-size: 10px; font-weight: 500; letter-spacing: 3px; text-transform: uppercase; color: rgba(237,232,223,0.15); }

/* ── MESSAGE ROWS ── */
.msg-row { display: flex; align-items: flex-end; gap: 10px; }
.msg-row.mine { flex-direction: row-reverse; }
.msg-avatar { width: 32px; height: 32px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.15); overflow: hidden; background: rgba(210,175,100,0.04); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 13px; color: rgba(210,175,100,0.4); flex-shrink: 0; }
.msg-avatar img { width: 100%; height: 100%; object-fit: cover; }

.bubble { max-width: 62%; padding: 13px 16px; position: relative; }
.bubble.theirs { border: 1px solid rgba(210,175,100,0.12); background: rgba(210,175,100,0.03); }
.bubble.mine { border: 1px solid rgba(210,175,100,0.28); background: rgba(210,175,100,0.08); }
.bubble-text { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.75); line-height: 1.7; letter-spacing: 0.2px; margin: 0 0 6px; }
.bubble-edited { font-size: 10px; font-style: italic; color: rgba(237,232,223,0.3); margin-left: 4px; }
.bubble-footer { display: flex; align-items: center; justify-content: flex-end; gap: 8px; position: relative; }
.bubble-time { font-size: 8px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(237,232,223,0.15); }
.bubble-menu-btn { background: none; border: none; color: rgba(237,232,223,0.2); cursor: pointer; font-size: 14px; line-height: 1; padding: 2px 4px; transition: color 0.2s; }
.bubble-menu-btn:hover { color: rgba(210,175,100,0.7); }
.bubble-menu { position: absolute; top: 100%; right: 0; margin-top: 4px; background: #0a0a0b; border: 1px solid rgba(210,175,100,0.25); z-index: 15; min-width: 100px; }
.bubble-menu-item { display: block; width: 100%; text-align: left; padding: 10px 14px; background: none; border: none; color: rgba(237,232,223,0.75); font-size: 12px; cursor: pointer; font-family: 'Montserrat', sans-serif; transition: background 0.2s; }
.bubble-menu-item:hover { background: rgba(210,175,100,0.08); }
.bubble-menu-item.destructive { color: rgba(220,100,85,0.85); }

/* ── EDIT MESSAGE PANEL ── */
.edit-panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.2); width: 100%; max-width: 420px; padding: 22px; }
.edit-panel-hd { font-family: 'Cormorant', serif; font-size: 18px; color: #edeae2; margin-bottom: 14px; }
.edit-textarea { width: 100%; background: transparent; border: 1px solid rgba(210,175,100,0.25); color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 300; padding: 10px 12px; outline: none; resize: vertical; }
.edit-textarea:focus { border-color: rgba(210,175,100,0.5); }
.edit-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }
.edit-cancel, .edit-save { background: none; border: none; font-size: 11px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; cursor: pointer; padding: 8px 12px; font-family: 'Montserrat', sans-serif; }
.edit-cancel { color: rgba(237,232,223,0.4); }
.edit-cancel:hover { color: rgba(237,232,223,0.7); }
.edit-save { color: rgba(210,175,100,0.7); }
.edit-save:hover { color: rgba(210,175,100,1); }

/* ── INPUT BAR ── */
.input-bar { position: relative; z-index: 10; display: flex; align-items: center; gap: 16px; padding: 16px 32px 24px; border-top: 1px solid rgba(210,175,100,0.1); background: rgba(7,7,8,0.95); flex-shrink: 0; }
.share-btn { width: 40px; height: 40px; border: 1px solid rgba(210,175,100,0.25); background: transparent; color: rgba(210,175,100,0.5); display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: border-color 0.2s, color 0.2s; }
.share-btn:hover { border-color: rgba(210,175,100,0.6); color: rgba(210,175,100,0.9); }
.share-btn svg { width: 16px; height: 16px; }
.msg-input { flex: 1; background: transparent; border: none; border-bottom: 1px solid rgba(237,232,223,0.1); padding: 10px 0; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); transition: border-color 0.3s; letter-spacing: 0.3px; }
.msg-input::placeholder { color: rgba(237,232,223,0.15); font-style: italic; }
.msg-input:focus { border-bottom-color: rgba(210,175,100,0.35); }
.input-line { position: absolute; bottom: 24px; left: 32px; height: 1px; width: 0; background: linear-gradient(90deg, rgba(210,175,100,0.6), rgba(210,175,100,0.1)); transition: width 0.4s cubic-bezier(0.25,0.46,0.45,0.94); pointer-events: none; }
.msg-input:focus ~ .input-line { width: calc(100% - 80px); }
.send-btn { width: 44px; height: 44px; border: 1px solid rgba(210,175,100,0.25); background: transparent; color: rgba(210,175,100,0.5); display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: border-color 0.2s, color 0.2s; }
.send-btn:not(:disabled):hover { border-color: rgba(210,175,100,0.6); color: rgba(210,175,100,0.9); }
.send-btn:disabled { opacity: 0.25; cursor: not-allowed; }
.send-btn svg { width: 16px; height: 16px; }

/* ── SHARED POST CARD (inside bubble) ── */
.shared-post { border: 1px solid rgba(210,175,100,0.3); background: rgba(210,175,100,0.05); padding: 12px 14px; margin-bottom: 8px; cursor: pointer; transition: border-color 0.2s; }
.shared-post:hover { border-color: rgba(210,175,100,0.6); }
.shared-post-tags { display: flex; gap: 6px; margin-bottom: 6px; }
.shared-post-tag { font-size: 7.5px; font-weight: 600; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(210,175,100,0.6); border: 1px solid rgba(210,175,100,0.25); padding: 2px 6px; }
.shared-post-name { font-family: 'Cormorant', serif; font-size: 17px; font-weight: 400; color: #edeae2; line-height: 1.2; margin-bottom: 6px; }
.shared-post-cta { font-size: 8px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.5); }

/* ── SHARE PICKER OVERLAY ── */
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.82); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 24px; }
.share-panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.2); width: 100%; max-width: 420px; max-height: 70vh; display: flex; flex-direction: column; }
.share-panel-hd { display: flex; align-items: center; justify-content: space-between; padding: 20px 22px; border-bottom: 1px solid rgba(210,175,100,0.1); font-family: 'Cormorant', serif; font-size: 18px; color: #edeae2; }
.share-close { background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.3); display: flex; transition: color 0.2s; }
.share-close:hover { color: rgba(210,175,100,0.7); }
.share-close svg { width: 16px; height: 16px; }
.share-search-wrap { padding: 14px 22px 0; }
.share-search-input { width: 100%; background: rgba(210,175,100,0.05); border: 1px solid rgba(210,175,100,0.2); color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 300; padding: 10px 12px; outline: none; }
.share-search-input::placeholder { color: rgba(237,232,223,0.25); font-style: italic; }
.share-search-input:focus { border-color: rgba(210,175,100,0.5); }
.share-loading, .share-empty { padding: 32px 22px; font-size: 12px; color: rgba(237,232,223,0.3); line-height: 1.6; }
.share-item-by { font-size: 9px; color: rgba(237,232,223,0.3); margin-top: 3px; }
.share-new-btn { background: none; border: 1px solid rgba(210,175,100,0.3); color: rgba(210,175,100,0.7); font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; padding: 10px 16px; cursor: pointer; font-family: 'Montserrat', sans-serif; transition: border-color 0.2s, color 0.2s; margin-top: 12px; }
.share-new-btn:hover { border-color: rgba(210,175,100,0.7); color: rgba(210,175,100,1); }
.share-new-btn-footer { margin: 12px 22px 18px; }
.share-list { overflow-y: auto; padding: 8px; }
.share-item { padding: 14px 16px; cursor: pointer; border-bottom: 1px solid rgba(210,175,100,0.06); transition: background 0.2s; }
.share-item:last-child { border-bottom: none; }
.share-item:hover { background: rgba(210,175,100,0.06); }
.share-item-tags { font-size: 8px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.45); margin-bottom: 4px; }
.share-item-name { font-family: 'Cormorant', serif; font-size: 17px; font-weight: 400; color: #edeae2; }

@media (max-width: 600px) { .chat-header, .input-bar { padding-left: 20px; padding-right: 20px; } .messages-area { padding: 20px; } .frame, .frame-inner { inset: 12px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>